import { useEffect, useState } from 'react'
import {
  landingFetchCampaignTopDonors,
  type LandingTopDonor,
} from '@/lib/landing-top-donors'

export type LandingTopDonorsPhase = 'idle' | 'loading' | 'ready' | 'error'

export type UseLandingCampaignTopDonorsResult = {
  items: LandingTopDonor[]
  viewerTotalAmount: string | null
  viewerRank: number | null
  phase: LandingTopDonorsPhase
  errorMessage: string | null
}

const RANKING_ERROR_MESSAGE =
  'Não foi possível carregar o ranking. Tente de novo em instantes.'

type RankingState = {
  resolvedId: number | null
  resolvedRefreshKey: number
  items: LandingTopDonor[]
  viewerTotalAmount: string | null
  viewerRank: number | null
  errorMessage: string | null
}

const INITIAL_STATE: RankingState = {
  resolvedId: null,
  resolvedRefreshKey: -1,
  items: [],
  viewerTotalAmount: null,
  viewerRank: null,
  errorMessage: null,
}

export function useLandingCampaignTopDonors(
  campaignId: number | null | undefined,
  refreshKey = 0
): UseLandingCampaignTopDonorsResult {
  const [state, setState] = useState<RankingState>(INITIAL_STATE)

  useEffect(() => {
    if (campaignId == null || campaignId <= 0) {
      return
    }

    let cancelled = false

    void landingFetchCampaignTopDonors(campaignId)
      .then((payload) => {
        if (cancelled) {
          return
        }
        setState({
          resolvedId: campaignId,
          resolvedRefreshKey: refreshKey,
          items: payload.items,
          viewerTotalAmount: payload.viewer_total_amount ?? null,
          viewerRank: payload.viewer_rank ?? null,
          errorMessage: null,
        })
      })
      .catch(() => {
        if (cancelled) {
          return
        }
        setState({
          resolvedId: campaignId,
          resolvedRefreshKey: refreshKey,
          items: [],
          viewerTotalAmount: null,
          viewerRank: null,
          errorMessage: RANKING_ERROR_MESSAGE,
        })
      })

    return () => {
      cancelled = true
    }
  }, [campaignId, refreshKey])

  if (campaignId == null || campaignId <= 0) {
    return {
      items: [],
      viewerTotalAmount: null,
      viewerRank: null,
      phase: 'idle',
      errorMessage: null,
    }
  }

  if (state.resolvedId !== campaignId || state.resolvedRefreshKey !== refreshKey) {
    return {
      items: [],
      viewerTotalAmount: null,
      viewerRank: null,
      phase: 'loading',
      errorMessage: null,
    }
  }

  if (state.errorMessage) {
    return {
      items: [],
      viewerTotalAmount: null,
      viewerRank: null,
      phase: 'error',
      errorMessage: state.errorMessage,
    }
  }

  return {
    items: state.items,
    viewerTotalAmount: state.viewerTotalAmount,
    viewerRank: state.viewerRank,
    phase: 'ready',
    errorMessage: null,
  }
}
